import { KEY_PERFORMANCE_AREAS } from "@/lib/constants";
import React, { useState } from "react";

import { Button } from "ui/index";
import { Form } from "react-crud-form";
import { RatingGuide } from "@/modules/performance/components/rating-guide";
import { SectionPanel } from "@/modules/performance/components/section-panel";
import { StepperNav } from "@/modules/performance/components/stepper-nav";
import { useParams } from "react-router-dom";
import { usePerformanceForm } from "@/modules/performance/components/usePerformanceForm";
import { useNavigate } from "react-router-dom";
import { invalidateAllQueries } from "utils/utils";
import EmployeeSection from "@/modules/performance/components/section/employee";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";
import { useEffect, useMemo } from "react";
import classNames from "classnames";
import { useQuad } from "@/modules/performance/context";
import { useCompetencyData } from "@/modules/performance/hooks/useCompetencyData";
import Loader from "@/components/loader";

const getKeyPerformanceAreas = () => {
  return Object.keys(KEY_PERFORMANCE_AREAS).map((sectionTitle) => ({
    title: sectionTitle,
    items: KEY_PERFORMANCE_AREAS[sectionTitle].map((q) => ({
      key: q.id,
      label: q.competency,
      description: q.expectation,
      type: q.type,
    })),
  }));
};

const PerformanceForm = ({
  onSubmit,
  userRole: initialUserRole = "A1",
  selfEvaluation = false,
  performanceId,
  showEmployee = false,
}) => {
  const { quadId } = useParams();
  const { isQuadCompleted } = useQuad();
  const { grade, role } = useSelector(selectUser);
  const [currentUserRole, setCurrentUserRole] = useState(initialUserRole);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const {
    data: competencySections = [],
    isLoading: isLoadingCompetencies,
    error: competencyError,
  } = useCompetencyData(currentUserRole);

  const keyPerformanceAreas = getKeyPerformanceAreas();

  const sections = useMemo(() => {
    if (isLoadingCompetencies) {
      return [];
    }
    return [...competencySections, ...keyPerformanceAreas];
  }, [competencySections, keyPerformanceAreas, isLoadingCompetencies]);

  const { form, handleCreate, handleRead, handleUpdate } = usePerformanceForm({
    selfEvaluation,
    sections,
    performanceId,
  });

  const reviewee = form.watch("reviewee");

  useEffect(() => {
    if (selfEvaluation) {
      const userGrade = grade?.name || role?.name;
      if (userGrade && userGrade !== currentUserRole) {
        setCurrentUserRole(userGrade);
        setActiveStep(0); // Reset to first step when grade/role changes
      }
    } else {
      const revieweeGrade = reviewee?.grade?.name;
      console.log("reviewee grade", revieweeGrade);

      if (revieweeGrade && revieweeGrade !== currentUserRole) {
        setCurrentUserRole(revieweeGrade);
        setActiveStep(0);
      }
    }
  }, [grade, role, reviewee, selfEvaluation, currentUserRole]);

  const handleNext = () =>
    setActiveStep((s) => Math.min(s + 1, sections.length - 1));
  const handlePrev = () => setActiveStep((s) => Math.max(s - 1, 0));

  const onSubmitSuccess = () => {
    onSubmit?.(form.getValues?.() ?? {});
    invalidateAllQueries();
    navigate(-1);
  };

  const section = sections[activeStep];

  const formId = selfEvaluation ? "self-evaluation" : "+";

  if (isLoadingCompetencies || sections.length === 0) {
    return <Loader />;
  }

  // Show error state if competency fetch failed
  if (competencyError) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <p className="text-red-600">Failed to load competencies</p>
          <p className="text-gray-500 text-sm mt-2">
            Please try refreshing the page
          </p>
        </div>
      </div>
    );
  }

  return (
    <div key={quadId} className="flex-1">
      <Form
        form={form}
        create={handleCreate}
        read={handleRead}
        update={handleUpdate}
        itemId={performanceId || formId}
      >
        {({ isCreating, createItem, isNewItem, updateItem, isUpdating }) => {
          return (
            <div className="space-y-6">
              <div
                className={classNames({
                  "grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6 items-start px-4 lg:px-6 pt-6":
                    showEmployee,
                })}
              >
                <StepperNav
                  sections={sections}
                  activeIndex={activeStep}
                  onStep={setActiveStep}
                />
                {showEmployee && reviewee && (
                  <EmployeeSection title="Employee" employee={reviewee} />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6 items-start px-4 lg:px-6">
                <div className="space-y-4">
                  <SectionPanel
                    control={form.control}
                    section={section}
                    disabled={isQuadCompleted()}
                  />
                  <div className="flex items-center justify-between pt-4">
                    <div>
                      {activeStep > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handlePrev}
                        >
                          Previous
                        </Button>
                      )}
                    </div>
                    <div>
                      {activeStep < sections.length - 1 ? (
                        <Button type="button" onClick={handleNext}>
                          Next
                        </Button>
                      ) : isNewItem ? (
                        <Button
                          type="button"
                          onClick={() => createItem().then(onSubmitSuccess)}
                          disabled={isQuadCompleted()}
                        >
                          {isCreating ? "Submitting..." : "Submit"}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => updateItem().then(onSubmitSuccess)}
                          disabled={isQuadCompleted()}
                        >
                          {isUpdating ? "Submitting..." : "Submit"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {activeStep !== sections.length - 1 && <RatingGuide />}
              </div>
            </div>
          );
        }}
      </Form>
    </div>
  );
};

export default PerformanceForm;
